import { useCallback,useState, useEffect } from 'react';
import {
  Container, Paper, Table, TableRow, TableCell,
  TableHead, TableBody, Typography, Button,
  IconButton, Box, TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FormModal from '../components/FormModal';
import { useNotify } from '../context/NotificationContext';
import api from '../api/axios';
import Rating from '@mui/material/Rating';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

export default function Testimonials() {
  const { notify } = useNotify();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', message: '', rating: 5 });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const loadTestimonials =useCallback(async () => {
    api.get('/testimonials')
      .then(res => setList(res.data))
      .catch(() => notify('Failed to load testimonials', 'error'));
  },[notify]);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  const openModal = (t = null) => {
    if (t) setForm({ ...t });
    else setForm({ name: '', message: '', rating: 5 });
    setOpen(true);
  };

  const save = async () => {
    try {
      form.id
        ? await api.put(`/testimonials/${form.id}`, form)
        : await api.post('/testimonials', form);

      setOpen(false);
      loadTestimonials();
      notify(form.id ? 'Testimonial updated successfully' : 'Testimonial added successfully');
    } catch (err) {
      if (err.response?.status === 422) {
        const messages = Object.values(err.response.data.errors).flat();
        messages.forEach(msg => notify(msg, 'error'));
      } else {
        notify('Something went wrong', 'error');
      }
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const remove = async () => {
    try {
      await api.delete(`/testimonials/${deleteId}`);
      setConfirmOpen(false);
      loadTestimonials();
      notify('Testimonial deleted successfully');
    } catch (err) {
      notify('Failed to delete testimonial', 'error');
    }
  };

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h4" fontWeight={600}>Testimonials</Typography>
        <Button variant="contained" onClick={() => openModal()}>Add Testimonial</Button>
      </Box>

      <Paper elevation={3} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ background: '#111827' }}>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}><b>Name</b></TableCell>
              <TableCell sx={{ color: '#fff' }}><b>Message</b></TableCell>
              <TableCell sx={{ color: '#fff' }}><b>Rating</b></TableCell>
              <TableCell width={120} sx={{ color: '#fff' }}><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {list.map(t => (
              <TableRow key={t.id} hover>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.message}</TableCell>
                <TableCell>
                  <Rating value={t.rating} readOnly max={5} precision={1} />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => openModal(t)}><EditIcon /></IconButton>
                  <IconButton color="error" onClick={() => confirmDelete(t.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}

            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No testimonials found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal for Add/Edit */}
      <FormModal open={open} title={form.id ? 'Edit Testimonial' : 'Add Testimonial'} onClose={() => setOpen(false)} onSubmit={save}>
        <TextField
          label="Name"
          fullWidth
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Message"
          fullWidth
          multiline
          rows={3}
          value={form.message}
          onChange={e => setForm({ ...form, message: e.target.value })}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ mr: 2 }}>Rating</Typography>
          <Rating
            value={form.rating || 5}
            onChange={(e, val) => setForm({ ...form, rating: val })}
            max={5}
            precision={1}
          />
        </Box>
      </FormModal>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{
          sx: {
            background: "#111827",
            color: "#fff",
            borderRadius: 2,
          },
        }}>
        <DialogTitle>Delete Testimonial?</DialogTitle>
        <DialogContent>
         Are you sure you want to delete this testimonial?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={remove} variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
